/**
 * CORE-881 / CORE-990 — «Финансы → Продажи» мастера.
 *
 * Зеркало пользовательских «Покупок» со стороны того, кто продаёт. Не CRM:
 * здесь агрегаты, список операций и условия расчёта — всё, что нужно, чтобы
 * понять «сколько я заработал». Списка клиентов с контактами тут нет.
 *
 * Все расчётные поля необязательные, и это не небрежность типизации: пока
 * комиссия не утверждена (CORE-13), сервер присылает `null`, а не ноль. Ноль
 * означал бы «мастер получает всё» — и это неправда, которую экран не должен
 * повторять.
 */

/** Период отчёта. Те же коды понимает сервер. */
export type FinancePeriod = '7d' | '30d' | 'all';

export const FINANCE_PERIODS: { code: FinancePeriod; label: string }[] = [
  { code: '7d', label: '7 дней' },
  { code: '30d', label: '30 дней' },
  { code: 'all', label: 'Всё время' },
];

/** Условия, по которым посчитан отчёт. Приходят вместе с цифрами, а не «известны фронту». */
export interface MasterSalesTerms {
  /** false — расчётные поля придут null. */
  configured: boolean;
  /** Комиссия платформы в базисных пунктах: 3000 = 30%. */
  commissionRateBps: number | null;
  /** Сколько дней начисление держится до выплаты. */
  holdDays: number | null;
  payoutPeriod: 'monthly' | 'biweekly' | 'onRequest' | null;
  /** Почему условий нет. `COMMISSION_NOT_CONFIGURED` — ставка не задана. */
  reason: string | null;
}

/**
 * Состояние выплат по строке.
 *
 * «Выплачено» здесь не появится, пока нет реестра выплат: утверждать, что
 * деньги ушли, серверу сегодня не из чего.
 */
export type PayoutStatus = 'notConfigured' | 'hold' | 'available' | 'nothing';

/**
 * Продажи одной программы в одной валюте.
 *
 * Строка — это программа И валюта, а не просто программа: сложить рубли с
 * долларами нельзя, а курса у платформы нет.
 */
export interface ProgramSalesRow {
  programId: number;
  productType: 'program' | 'paidPractice';
  title: string | null;
  coverImageUrl: string | null;
  programStatus: 'draft' | 'published' | 'archived';
  currency: string;
  /** Продажи за период, включая те, что потом вернули: продажа была. */
  paidCount: number;
  gross: number;
  refundedCount: number;
  refunded: number;
  /** Gross за вычетом возвратов — база, от которой считается комиссия. */
  net: number;
  commission: number | null;
  masterAccrual: number | null;
  inHold: number | null;
  availableForPayout: number | null;
  payoutStatus: PayoutStatus;
}

export interface MasterSalesTotals {
  currency: string;
  paidCount: number;
  gross: number;
  refundedCount: number;
  refunded: number;
  net: number;
  commission: number | null;
  masterAccrual: number | null;
  inHold: number | null;
  availableForPayout: number | null;
}

export interface MasterSalesReport {
  period: FinancePeriod;
  terms: MasterSalesTerms;
  /** Итоги по валютам. Каждая валюта отдельной строкой. */
  totals: MasterSalesTotals[];
  items: ProgramSalesRow[];
  totalCount: number;
}

/**
 * Одна операция списка.
 *
 * Из персональных данных покупателя — только отображаемое имя: то же, что
 * мастер и так видит в чате программы.
 */
export interface SalesOperation {
  purchaseId: number;
  occurredAtUtc: string;
  programId: number;
  productType: 'program' | 'paidPractice';
  title: string | null;
  amount: number;
  currency: string;
  status: 'paid' | 'refunded';
  commission: number | null;
  masterAccrual: number | null;
  /** Величина возврата. Отрицательной не бывает — знак дело экрана. */
  refund: number | null;
  buyerName: string | null;
}

export interface SalesOperationList {
  period: FinancePeriod;
  terms: MasterSalesTerms;
  items: SalesOperation[];
  totalCount: number;
}

/** Компактные продажи для карточки программы в «Созданном»: ровно два числа. */
export interface ProgramSalesCompact {
  paidCount: number;
  /** Сумма продаж. Null, если продажи шли в разных валютах. */
  gross: number | null;
  currency: string | null;
  mixedCurrency: boolean;
}
